<script lang="ts">
  import {createEventDispatcher} from 'svelte';

  import type {ImageFile} from '../../types/project';

  import ImageEditor from '../image-editor/ImageEditor.svelte';
  import LoadingSpinner from '../loader/LoadingSpinner.svelte';
  import Button from '../button/Button.svelte';

  const dispatch = createEventDispatcher();

  export let image: ImageFile | null = null;

  let imageElement: HTMLImageElement;
  let focusPointRelative = {x: 0.5, y: 0.5};
  let focusPointAbsolute = image?.focusPoint
    ? {x: image.focusPoint.x, y: image.focusPoint.y}
    : null;
  $: aspectRatio = image ? image.width / image.height : 0;

  // Handle focus point change
  function handleSetFocusPoint(event: MouseEvent) {
    const imageRect = imageElement.getBoundingClientRect();
    const absoluteX = event.clientX - imageRect.x;
    const absoluteY = event.clientY - imageRect.y;

    focusPointAbsolute = {x: absoluteX, y: absoluteY};

    const relativeX = Math.round((absoluteX / imageRect.width) * 100) / 100;
    const relativeY = Math.round((absoluteY / imageRect.height) * 100) / 100;
    // Increase focus point values exponentially from the image center
    // to get a better result when placing the image with
    // background-position / object-position.
    const distanceToCenterX = 0.5 - relativeX;
    const distanceToCenterY = 0.5 - relativeY;
    const signX = Math.sign(distanceToCenterX);
    const signY = Math.sign(distanceToCenterY);
    const x = 0.5 - Math.sqrt(Math.abs(distanceToCenterX) / 0.5) * 0.5 * signX;
    const y = 0.5 - Math.sqrt(Math.abs(distanceToCenterY) / 0.5) * 0.5 * signY;

    focusPointRelative = {x, y};
  }
</script>

<style lang="postcss">
  .loadingContainer {
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }

  .canvasWrapper {
    position: relative;
    padding-bottom: calc(100% / 16 * 9); /* aspect ratio 16:9 */
    border: 1px solid var(--ubilabs-gray);
    background-color: var(--ubilabs-gray);
  }

  .canvasWrapper > :global(div) {
    position: absolute;
    top: 0;
    left: 0;
  }

  .imageContainer {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .imageContainer > div {
    position: relative;
  }

  .imageContainer .imageHigher {
    width: 100%;
  }
  .imageContainer .imageWider {
    height: 100%;
  }

  .focusPoint {
    position: absolute;
    translate: transform(-50%, -50%);
    width: 1rem;
    height: 1rem;
    border: 1px solid var(--ubilabs-white);
    border-radius: 0.5rem;
    background-color: var(--ubilabs-purple);
    pointer-events: none;
  }

  .imagePreviewWrapper {
    max-width: 10rem;
    max-height: 10rem;
    margin-bottom: 0.5rem;
  }

  .imagePreview {
    padding-bottom: calc(100% / 16 * 9); /* aspect ratio 16:9 */
    overflow: hidden;
    border: 1px solid var(--ubilabs-gray);
    background-color: var(--ubilabs-gray);
    background-size: cover;
    background-repeat: no-repeat;
  }
</style>

<ImageEditor on:close let:closeEditor>
  <div slot="content">
    <div class="canvasWrapper">
      {#if !image}
        <div class="loadingContainer"><LoadingSpinner /></div>
      {:else}
        <div class="imageContainer">
          <div
            class:imageHigher={aspectRatio > 16 / 9}
            class:imageWider={aspectRatio <= 16 / 9}>
            <img
              src={image.url}
              alt={image.name}
              bind:this={imageElement}
              on:click={handleSetFocusPoint} />

            {#if focusPointAbsolute}
              <div
                class="focusPoint"
                style="left: {focusPointAbsolute.x}px; top: {focusPointAbsolute.y}px" />
            {/if}
          </div>
        </div>
      {/if}
    </div>
  </div>

  <div slot="actions">
    <div class="imagePreviewWrapper">
      <div
        class="imagePreview"
        style="background-image: url('{image?.url}'); background-position:
        {focusPointRelative.x * 100}% {focusPointRelative.y * 100}%;" />
    </div>

    <Button
      on:click={() => {
        dispatch('setfocus', {
          name: image?.name,
          focusPoint: focusPointRelative
        });

        closeEditor();
      }}>
      Save focus
    </Button>
  </div>
</ImageEditor>
